import mbxGeocoding, { GeocodeFeature } from '@mapbox/mapbox-sdk/services/geocoding';

// Interfacce per i tipi di geocoding
export interface PlaceOption {
  label: string;
  value: string;
  center?: [number, number];
}

export interface GeocodingResult {
  placeName: string;
  coordinates: [number, number];
  language: string;
}

export interface LocalizationResult {
  originalName: string;
  localizedName: string;
  language: string;
}

class GeocodingService {
  private geocodingClient: any;
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private CACHE_DURATION = 10 * 60 * 1000; // 10 minuti

  constructor() {
    this.geocodingClient = mbxGeocoding({ 
      accessToken: process.env.REACT_APP_MAPBOX_TOKEN! 
    });
  }

  /**
   * Ottiene suggerimenti di luoghi per autocompletamento
   */
  async getPlaceSuggestions(
    query: string, 
    language: string = 'en', 
    limit: number = 5
  ): Promise<PlaceOption[]> {
    const cacheKey = `suggestions_${query}_${language}_${limit}`;
    
    // Controlla cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await this.geocodingClient
        .forwardGeocode({
          query,
          autocomplete: true,
          limit,
          language: [language]
        })
        .send();

      const options: PlaceOption[] = response.body.features.map((feature: GeocodeFeature) => ({
        label: feature.place_name,
        value: feature.place_name,
        center: feature.center as [number, number]
      }));

      // Salva in cache
      this.cache.set(cacheKey, {
        data: options,
        timestamp: Date.now()
      });

      return options;
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      throw new Error(`Errore nel recupero suggerimenti luoghi: ${error}`);
    }
  }

  /**
   * Ottiene coordinate e nome in inglese per un luogo
   */
  async getEnglishCoordinates(
    query: string
  ): Promise<GeocodingResult> {
    const cacheKey = `english_coords_${query}`;
    
    // Controlla cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await this.geocodingClient
        .forwardGeocode({
          query,
          limit: 1,
          language: ['en']
        })
        .send();

      if (response.body.features && response.body.features.length > 0) {
        const feature = response.body.features[0];
        const result: GeocodingResult = {
          placeName: feature.place_name,
          coordinates: feature.geometry.coordinates as [number, number],
          language: 'en'
        };

        // Salva in cache
        this.cache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });

        return result;
      } else {
        throw new Error('Nessun risultato trovato per la geocodifica');
      }
    } catch (error) {
      console.error('Error getting English coordinates:', error);
      throw new Error(`Errore nella geocodifica inglese: ${error}`);
    }
  }

  /**
   * Localizza un nome di luogo in una lingua specifica
   */
  async localizePlaceName(
    englishName: string, 
    targetLanguage: string
  ): Promise<LocalizationResult> {
    // Se la lingua target è inglese, non serve localizzazione
    if (targetLanguage === 'en') {
      return {
        originalName: englishName,
        localizedName: englishName,
        language: targetLanguage
      };
    }

    const cacheKey = `localization_${englishName}_${targetLanguage}`;
    
    // Controlla cache
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await this.geocodingClient
        .forwardGeocode({
          query: englishName,
          limit: 1,
          language: [targetLanguage]
        })
        .send();

      let localizedName = englishName;
      
      if (response.body.features && response.body.features.length > 0) {
        localizedName = response.body.features[0].place_name;
      }

      const result: LocalizationResult = {
        originalName: englishName,
        localizedName,
        language: targetLanguage
      };

      // Salva in cache
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('Error localizing place name:', error);
      // In caso di errore, ritorna il nome originale
      return {
        originalName: englishName,
        localizedName: englishName,
        language: targetLanguage
      };
    }
  }

  /**
   * Localizza multipli marker in una lingua specifica
   */
  async localizeMultipleMarkers(
    markers: Array<{ id: string; name: string; [key: string]: any }>,
    targetLanguage: string
  ): Promise<Array<{ id: string; name: string; localizedName: string; [key: string]: any }>> {
    if (targetLanguage === 'en') {
      return markers.map(marker => ({
        ...marker,
        localizedName: marker.name
      }));
    }

    try {
      const localizedMarkers = await Promise.all(
        markers.map(async (marker) => {
          const localization = await this.localizePlaceName(marker.name, targetLanguage);
          return {
            ...marker,
            localizedName: localization.localizedName
          };
        })
      );

      return localizedMarkers;
    } catch (error) {
      console.error('Error localizing multiple markers:', error);
      // In caso di errore, ritorna i marker con nome originale
      return markers.map(marker => ({
        ...marker,
        localizedName: marker.name
      }));
    }
  }

  /**
   * Genera URL per mappe statiche
   */
  generateStaticMapUrl(
    longitude: number, 
    latitude: number, 
    width: number = 200, 
    height: number = 90, 
    zoom: number = 6
  ): string {
    const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${longitude},${latitude},${zoom},0,0/${width}x${height}?access_token=${accessToken}`;
  }

  /**
   * Genera URL per mappa condivisa con marker
   */
  generateSharedMapUrl(
    markers: Array<{ longitude: number; latitude: number }>,
    width: number = 1200,
    height: number = 630,
    zoom: number = 5
  ): string {
    const accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const center = markers.length > 0
      ? `${markers[0].longitude},${markers[0].latitude}`
      : "0,0";

    const markerOverlay = markers
      .map(marker => `pin-s+FF0000(${marker.longitude},${marker.latitude})`)
      .join(',');

    return `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${markerOverlay}/${center},${zoom}/${width}x${height}?access_token=${accessToken}`;
  }

  /**
   * Pulisce la cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Ottiene statistiche cache
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

// Esporta istanza singleton
const geocodingService = new GeocodingService();
export default geocodingService;
